import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@config/axios/axios.config";
import { SERVER_ROUTES } from "@constants/api.routes";
import { showNotification } from "@libs/notistack/notificationStack";
import { SNACKBAR_SEVERITY } from "@constants/notificationStack";

export interface StudentSkill {
	uuid: string;
	skillUuid: string;
	skill?: {
		uuid: string;
		name: string;
	};
}

export interface StudentSkillsResponse {
	success: boolean;
	message: string;
	data: StudentSkill[];
}

export interface AddStudentSkillPayload {
	skillUuids: string[];
}

export interface UpdateStudentSkillPayload {
	skillUuid: string;
}

// Type guard helpers
function isSuccessResponseWithData(response: unknown): response is { success: boolean; message: string; data: StudentSkill } {
	return (
		typeof response === "object" &&
		response !== null &&
		"success" in response &&
		"data" in response &&
		"message" in response
	);
}

function isSuccessResponse(response: unknown): response is { success: boolean; message: string } {
	return (
		typeof response === "object" &&
		response !== null &&
		"success" in response &&
		"message" in response
	);
}

// Query to get student skills
export const useStudentSkills = (enabled: boolean = true) => {
	return useQuery<StudentSkill[]>({
		queryKey: ["student-skills"],
		queryFn: async () => {
			const response = await api.get<StudentSkillsResponse>(
				`${SERVER_ROUTES.INSTITUTES.STUDENT_SKILLS}`
			);

			// Handle axios interceptor response structure
			let responseData: StudentSkillsResponse;

			if (response && typeof response === "object" && "data" in response && response.data) {
				if (typeof response.data === "object" && "data" in response.data) {
					responseData = response.data as unknown as StudentSkillsResponse;
				} else {
					responseData = { success: true, message: "", data: response.data as StudentSkill[] };
				}
			} else {
				responseData = { success: true, message: "", data: [] };
			}

			return responseData.data || [];
		},
		enabled,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
};

// Mutation to add a new student skill
export const useAddStudentSkill = () => {
	const queryClient = useQueryClient();

	return useMutation<StudentSkill | StudentSkill[], Error, AddStudentSkillPayload>({
		mutationFn: async (payload: AddStudentSkillPayload) => {
			try {
				const response = await api.post(
					`${SERVER_ROUTES.INSTITUTES.STUDENT_SKILLS}`,
					payload
				);

				// Axios interceptor returns response.data, so response is already the data
				// Handle different response structures
				if (response && typeof response === "object") {
					// Case 1: { success: true, message: "...", data: {...} or [...] }
					if (isSuccessResponseWithData(response)) {
						if (!response.success) {
							throw new Error(response.message || "Failed to add skill");
						}
						return response.data;
					}
					// Case 2: Array of skills (when multiple skills are added)
					if (Array.isArray(response)) {
						return response as StudentSkill[];
					}
					// Case 3: Direct data object (if API returns just the skill object)
					if ("uuid" in response && "skillUuid" in response) {
						return response as StudentSkill;
					}
					// Case 4: Nested structure { data: { success, message, data: {...} or [...] } }
					if ("data" in response && typeof response.data === "object") {
						const nested = response.data as { success?: boolean; message?: string; data?: StudentSkill | StudentSkill[] };
						if (nested.success === false) {
							throw new Error(nested.message || "Failed to add skill");
						}
						if (nested.data) {
							return nested.data;
						}
					}
				}

				// If we get here, response structure is unexpected
				throw new Error("Unexpected response structure from server");
			} catch (error) {
				// Re-throw with better error message
				if (error instanceof Error) {
					throw error;
				}
				throw new Error("Failed to add skill. Please try again.");
			}
		},
		onSuccess: (data) => {
			// Invalidate student skills query to refetch the updated list
			queryClient.invalidateQueries({ queryKey: ["student-skills"] });
			const skillCount = Array.isArray(data) ? data.length : 1;
			showNotification({
				message: skillCount > 1 ? `Successfully added ${skillCount} skills` : "Skill added successfully",
				severity: SNACKBAR_SEVERITY.SUCCESS,
			});
		},
		onError: (error) => {
			// Handle Axios error response to get deep error message
			const axiosError = error as { response?: { data?: { message?: string } } };
			const errorMessage = axiosError?.response?.data?.message || error.message || "Failed to add skill";
			showNotification({
				message: errorMessage,
				severity: SNACKBAR_SEVERITY.ERROR,
			});
		},
	});
};

// Mutation to update a student skill (PATCH)
export const useUpdateStudentSkill = () => {
	const queryClient = useQueryClient();

	return useMutation<StudentSkill, Error, { uuid: string; payload: UpdateStudentSkillPayload }>({
		mutationFn: async ({ uuid, payload }) => {
			try {
				const response = await api.patch(
					`${SERVER_ROUTES.INSTITUTES.STUDENT_SKILLS}/${uuid}`,
					payload
				);

				// Axios interceptor returns response.data, so response is already the data
				// Handle different response structures
				if (response && typeof response === "object") {
					// Case 1: { success: true, message: "...", data: {...} }
					if (isSuccessResponseWithData(response)) {
						if (!response.success) {
							throw new Error(response.message || "Failed to update skill");
						}
						return response.data;
					}
					// Case 2: Direct data object (if API returns just the skill object)
					if ("uuid" in response && "skillUuid" in response) {
						return response as StudentSkill;
					}
					// Case 3: Nested structure { data: { success, message, data: {...} } }
					if ("data" in response && typeof response.data === "object") {
						const nested = response.data as { success?: boolean; message?: string; data?: StudentSkill };
						if (nested.success === false) {
							throw new Error(nested.message || "Failed to update skill");
						}
						if (nested.data) {
							return nested.data;
						}
					}
				}

				// If we get here, response structure is unexpected
				throw new Error("Unexpected response structure from server");
			} catch (error) {
				// Re-throw with better error message
				if (error instanceof Error) {
					throw error;
				}
				throw new Error("Failed to update skill. Please try again.");
			}
		},
		onSuccess: () => {
			// Invalidate student skills query to refetch the updated list
			queryClient.invalidateQueries({ queryKey: ["student-skills"] });
			showNotification({
				message: "Skill updated successfully",
				severity: SNACKBAR_SEVERITY.SUCCESS,
			});
		},
		onError: (error) => {
			showNotification({
				message: error.message || "Failed to update skill",
				severity: SNACKBAR_SEVERITY.ERROR,
			});
		},
	});
};

// Mutation to delete a student skill
export const useDeleteStudentSkill = () => {
	const queryClient = useQueryClient();

	return useMutation<void, Error, string>({
		mutationFn: async (uuid: string) => {
			try {
				const response = await api.delete(
					`${SERVER_ROUTES.INSTITUTES.STUDENT_SKILLS}/${uuid}`
				);

				// Axios interceptor returns response.data, so response is already the data
				// Handle different response structures
				if (response && typeof response === "object") {
					// Case 1: { success: true, message: "..." }
					if (isSuccessResponse(response)) {
						if (!response.success) {
							throw new Error(response.message || "Failed to delete skill");
						}
						return; // Delete operations typically don't return data
					}
					// Case 2: Empty response or 204 No Content (success)
					// If response exists but no success field, assume success
					return;
				}

				// If response is null/undefined, assume success (204 No Content)
				return;
			} catch (error) {
				// Re-throw with better error message
				if (error instanceof Error) {
					throw error;
				}
				throw new Error("Failed to delete skill. Please try again.");
			}
		},
		onSuccess: () => {
			// Invalidate student skills query to refetch the updated list
			queryClient.invalidateQueries({ queryKey: ["student-skills"] });
			showNotification({
				message: "Skill removed successfully",
				severity: SNACKBAR_SEVERITY.SUCCESS,
			});
		},
		onError: (error) => {
			showNotification({
				message: error.message || "Failed to remove skill",
				severity: SNACKBAR_SEVERITY.ERROR,
			});
		},
	});
};
